import { useQuery } from "react-query";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import LoadingWrapper from "components/Loading/LoadingWrapper";
import useAPI from "hooks/useAPI";
import Box from "@mui/material/Box";
import scss from "./ReclamationsDetails.module.scss";
import DeleteDialog from "components/Dialogs/DeleteDielog";
import ProfilePage from "pages/Profile/ProfilePage";
import Button from "components/Buttons/Button/Button";
import Table from "../../../../components/Table/Table";
import InsertCommentIcon from "@mui/icons-material/InsertComment";
import PaginationUI from "components/UI/PaginationUI";
import { useTranslation } from "react-i18next";

const ReclamationsDetails = () => {
    const { t } = useTranslation();
    const api = useAPI();
    const { id, reclamation_token } = useParams();
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(12);
    const [page, setPage] = useState(1);

    const [currentCommentPage, setCurrentCommentPage] = useState(1);
    const [allComments, setAllComments] = useState([]);

    const {
        data: reclamationsItemsComments,
        isLoading: isLoadingComments,
        refetch,
    } = useQuery(
        ["reclamationsItemsComments", selectedRowId, currentCommentPage],
        () =>
            api.list(`/reclamations/items/comments/${selectedRowId}`, {
                page: currentCommentPage,
            }),
        {
            enabled: !!selectedRowId,
            onSuccess: (data) => {
                if (currentCommentPage === 1) {
                    setAllComments(data.items);
                } else {
                    setAllComments((prev) => [...prev, ...data.items]);
                }
            },
        }
    );

    const handleLoadMore = () => {
        setCurrentCommentPage((prev) => prev + 1);
    };

    // Load all products in the cart
    const { data: reclamationsItems, isLoading } = useQuery(["reclamationsItems"], () => api.list(`/reclamations/items/${reclamation_token}`));

    const [showDialog, setShowDialog] = useState(false);

    let products = (reclamationsItems?.items ?? []).map((item) => ({
        code: item?.product_sku,
        name: item?.product_name,
        id_reclamations: item?.id_reclamations,
        fiscal_clip: item?.fiscal_clip,
        fiscal_clip_date: item?.fiscal_clip_date,
        product_name: item?.product_name,
        description: item?.description,
        responsive_type: (item?.responsive_type ?? []).map((type) => type?.name).join(", "),
        id: item?.id,
        id_product: item?.id_product,
    }));

    const handleRowClick = (id) => {
        setSelectedRowId(id);
    };

    useEffect(() => setCurrentPage(reclamationsItems?.pagination.selected_page ?? 1), [reclamationsItems, page, reclamationsItems?.pagination.selected_page]);

    return (
        <ProfilePage>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <h4 className="h-underline">
                    {t("reclamations-details-title")}: {id}
                </h4>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button label="global-default-text-Back" to="/reklamacije" styleLinkButton={{ margin: "0 0 0 2rem" }} />
                </Box>
            </Box>

            <LoadingWrapper isLoading={isLoading}>
                <div className={scss.wrapper}>
                    <div className={scss.itemsList}>
                        <h6 className={`${scss.title} underline`}>{t("reclamations-details-subtitle")}</h6>
                        <div style={{ maxHeight: "100%", overflow: "auto" }}>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>{t("reclamations-details-table_th-name")}</th>
                                        <th>{t("reclamations-details-table_th-code")}</th>
                                        <th>{t("reclamations-details-table_th-description")}</th>
                                        <th>{t("reclamations-details-table_th-responsive_type")}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products?.map((item, index) => {
                                        return (
                                            <tr key={index}>
                                                <td>
                                                    {item.id_product ? (
                                                        <Link
                                                            to={`/katalog/artikal/${item.id_product}`}
                                                            style={{ background: "inherit", color: "inherit", padding: "0", display: "flex", justifyContent: "center", margin: "0", width: "100%" }}
                                                        >
                                                            {item.name}
                                                        </Link>
                                                    ) : (
                                                        item.name
                                                    )}
                                                </td>
                                                <td>
                                                    {item.id_product ? (
                                                        <Link
                                                            to={`/katalog/artikal/${item.id_product}`}
                                                            style={{ background: "inherit", color: "inherit", padding: "0", display: "flex", justifyContent: "center", margin: "0", width: "100%" }}
                                                        >
                                                            {item.code}
                                                        </Link>
                                                    ) : (
                                                        item.code
                                                    )}
                                                </td>
                                                <td>{item.description}</td>
                                                <td>{item.responsive_type}</td>
                                                <td style={{ textAlign: "right" }}>
                                                    <InsertCommentIcon
                                                        onClick={() => {
                                                            handleRowClick(item.id);
                                                            refetch();
                                                            setTimeout(() => {
                                                                setShowDialog(true);
                                                            }, 200);
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {products?.length === 0 && (
                                        <tr>
                                            <td colSpan={5} style={{ paddingTop: "2rem" }}>
                                                {t("reclamations-details-message-no_data")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        <PaginationUI
                            activePage={currentPage}
                            setPage={setPage}
                            numberOfDisplayed={reclamationsItems?.pagination?.items_per_page ?? 12}
                            numberOfProducts={reclamationsItems?.pagination.total_items ?? 0}
                            changePage={() => {
                                api.list(`/reclamations/items/${id}/${reclamation_token}`, { limit: limit, page: page }).then((data) => {});
                            }}
                            page1={page}
                        />
                    </div>
                </div>
            </LoadingWrapper>

            <DeleteDialog
                openDeleteDialog={{ show: showDialog }}
                setOpenDeleteDialog={() => {
                    setShowDialog(false);
                    setAllComments([]);
                    setCurrentCommentPage(1);
                }}
                handleConfirm={() => {
                    setAllComments([]);
                    setShowDialog(false);
                    setCurrentCommentPage(1);
                }}
                title={"reclamations-details-delete_dialog-title"}
                description={""}
                nameOfButtonCancel={"reclamations-details-delete_dialog-button"}
                showDeleteButton={false}
                deafultIcon={false}
                children={
                    <>
                        {showDialog && allComments?.length === 0 ? (
                            <p>{t("reclamations-details-message-no_comment")}.</p>
                        ) : (
                            <>
                                {allComments.map((comment, index) => {
                                    return (
                                        <Box key={index}>
                                            <Box sx={{ margin: "0.2rem 0", display: "flex", flexDirection: "column", alignItems: "end" }}>
                                                <span className={scss.createdAt}>
                                                    {comment.user_first_name + " " + comment.user_last_name} / {comment.created_at}
                                                </span>
                                            </Box>
                                            <Box className={scss.chatBox} dangerouslySetInnerHTML={{ __html: comment.comment }} />
                                        </Box>
                                    );
                                })}
                                {reclamationsItemsComments?.pagination?.total_pages > currentCommentPage && (
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <Button buttonStyleBasic={{ fontSize: "13px" }} label={"Prikaži jos"} onClick={handleLoadMore} variant="outlined" sx={{ mt: 2 }} loading={isLoadingComments} />
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                }
            />
        </ProfilePage>
    );
};

export default ReclamationsDetails;
